import { Button, Card, DatePicker, Form, Input, InputNumber, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type PromiseRow = {
  id: string;
  student_id: string;
  demand_id?: string | null;
  promised_amount: number;
  promised_on: string;
  notes?: string | null;
  status: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
};

export default function FeePromisesPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canPromise = Boolean(
    user?.permissions?.includes('education.fee.promise') ||
      user?.permissions?.includes('education.fee.collect') ||
      user?.permissions?.includes('education.fee.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-fee-promises'],
    queryFn: () => api.get<PromiseRow[]>('/education/fees/promises'),
  });

  const create = useMutation({
    mutationFn: (values: {
      student_id: string;
      demand_id?: string;
      promised_amount: number;
      promised_on: dayjs.Dayjs;
      notes?: string;
    }) =>
      api.post('/education/fees/promises', {
        student_id: values.student_id,
        demand_id: values.demand_id || undefined,
        promised_amount: values.promised_amount,
        promised_on: values.promised_on.format('YYYY-MM-DD'),
        notes: values.notes,
      }),
    onSuccess: () => {
      message.success(t('feePromiseCreated'));
      queryClient.invalidateQueries({ queryKey: ['education-fee-promises'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const close = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.post(`/education/fees/promises/${id}/status`, { status }),
    onSuccess: () => {
      message.success(t('feePromiseUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-fee-promises'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('feePromises')}
      description={t('feePromisesIntro')}
      extra={<Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={query.isLoading}
            dataSource={query.data?.data ?? []}
            pagination={{ pageSize: 20 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: PromiseRow) =>
                  `${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim(),
              },
              { title: t('amount'), dataIndex: 'promised_amount', width: 110 },
              { title: t('promisedOn'), dataIndex: 'promised_on', width: 130 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('actions'),
                width: 220,
                render: (_: unknown, row: PromiseRow) =>
                  row.status === 'open' && canPromise ? (
                    <Space size={4}>
                      <Button size="small" onClick={() => close.mutate({ id: row.id, status: 'kept' })}>
                        {t('promiseKept')}
                      </Button>
                      <Button size="small" onClick={() => close.mutate({ id: row.id, status: 'broken' })}>
                        {t('promiseBroken')}
                      </Button>
                      <Button size="small" onClick={() => close.mutate({ id: row.id, status: 'cancelled' })}>
                        {t('cancel')}
                      </Button>
                    </Space>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Card>
        {canPromise ? (
          <Card size="small" title={t('recordFeePromise')}>
            <Form
              layout="vertical"
              style={{ maxWidth: 520 }}
              initialValues={{ promised_on: dayjs() }}
              onFinish={(values) => create.mutate(values)}
            >
              <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
                <EducationStudentSelect />
              </Form.Item>
              <Form.Item name="demand_id" label={t('feeDemand')} extra={t('optionalDemandHint')}>
                <Input />
              </Form.Item>
              <Form.Item name="promised_amount" label={t('amount')} rules={[{ required: true }]}>
                <InputNumber min={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="promised_on" label={t('promisedOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="notes" label={t('notes')}>
                <Input.TextArea rows={2} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={create.isPending}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
